import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Gift, DollarSign, MapPin, Calendar, FileText, Image,
  Loader2, ArrowLeft, ArrowRight, CheckCircle, AlertCircle,
  Sparkles, Users, Target, Clock, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';

const API = process.env.REACT_APP_BACKEND_URL;

const PROJECT_TYPES = [
  { value: 'interior_painting', label: 'Interior Painting', icon: '🎨' },
  { value: 'exterior_painting', label: 'Exterior Painting', icon: '🏠' },
  { value: 'flooring', label: 'Flooring Installation', icon: '🪵' },
  { value: 'kitchen_cabinet_refinishing', label: 'Kitchen Cabinet Refinishing', icon: '🗄️' },
  { value: 'kitchen_cabinet_refacing', label: 'Kitchen Cabinet Refacing', icon: '🚪' },
  { value: 'countertop_backsplash', label: 'Countertop & Backsplash', icon: '💎' },
  { value: 'bathroom_renovation', label: 'Bathroom Renovation', icon: '🚿' },
  { value: 'interior_design_staging', label: 'Interior Design & Staging', icon: '🛋️' },
  { value: 'cleaning', label: 'Deep Cleaning', icon: '✨' },
  { value: 'real_estate_photography', label: 'Real Estate Photography', icon: '📸' },
  { value: 'roof_repair', label: 'Roof Repair', icon: '🏗️' },
  { value: 'landscaping', label: 'Landscaping', icon: '🌳' },
  { value: 'hvac', label: 'HVAC Service', icon: '❄️' },
  { value: 'other', label: 'Other', icon: '📦' },
];

const CITIES = [
  { value: 'Toronto', province: 'Ontario' },
  { value: 'Vancouver', province: 'British Columbia' },
  { value: 'Calgary', province: 'Alberta' },
  { value: 'Ottawa', province: 'Ontario' },
  { value: 'Montreal', province: 'Quebec' },
  { value: 'Edmonton', province: 'Alberta' },
  { value: 'Winnipeg', province: 'Manitoba' },
  { value: 'Halifax', province: 'Nova Scotia' },
];

const SponsorCreateGiveaway = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sponsorProfile, setSponsorProfile] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_type: '',
    value: '',
    image_url: '',
    city: '',
    province_state: '',
    end_date: '',
    contractor_name: '',
    skill_testing_question: 'What is 15 + 27?',
    skill_testing_answer: '42',
    featured: false
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchSponsorProfile();
  }, [token]);

  const fetchSponsorProfile = async () => {
    try {
      const response = await fetch(`${API}/api/profile/sponsor`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSponsorProfile(data);
      }
    } catch (error) {
      console.error('Error fetching sponsor profile:', error);
    }
  };

  const validateStep = (stepNum) => {
    const newErrors = {};
    
    if (stepNum === 1) {
      if (!formData.title.trim()) newErrors.title = 'Title is required';
      if (!formData.project_type) newErrors.project_type = 'Project type is required';
      if (!formData.description.trim()) newErrors.description = 'Description is required';
    }
    
    if (stepNum === 2) {
      if (!formData.value || parseFloat(formData.value) < 500) {
        newErrors.value = 'Value must be at least $500';
      }
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.end_date) newErrors.end_date = 'End date is required';
      
      // Check end date is at least 7 days from now
      const endDate = new Date(formData.end_date);
      const minDate = new Date();
      minDate.setDate(minDate.getDate() + 7);
      if (endDate < minDate) {
        newErrors.end_date = 'End date must be at least 7 days from now';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleCityChange = (city) => {
    const cityData = CITIES.find(c => c.value === city);
    setFormData({
      ...formData,
      city,
      province_state: cityData?.province || ''
    });
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/giveaways`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          value: parseFloat(formData.value),
          end_date: new Date(formData.end_date).toISOString(),
          image_url: formData.image_url || getDefaultImage(formData.project_type)
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create giveaway');
      }

      const giveaway = await response.json();
      toast.success('Giveaway created successfully!');
      navigate(`/giveaways/${giveaway.giveaway_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultImage = (projectType) => {
    const images = {
      interior_painting: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800',
      exterior_painting: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
      flooring: 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=800',
      kitchen_cabinet_refinishing: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800',
      bathroom_renovation: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800',
      cleaning: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800',
    };
    return images[projectType] || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800';
  };

  const progress = (step / 3) * 100;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="create-giveaway-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
          <Gift className="w-6 h-6 text-[#FF3B30]" />
          Create a Giveaway
        </h1>
        <p className="text-neutral-500 mt-1">
          Launch a sponsored giveaway to capture high-intent homeowner leads
        </p>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-xl border border-neutral-200 p-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-neutral-600">Step {step} of 3</span>
          <span className="text-neutral-400">{Math.round(progress)}% complete</span>
        </div>
        <Progress value={progress} className="h-2" />
        <div className="flex justify-between mt-3">
          <span className={`text-xs ${step >= 1 ? 'text-[#FF3B30] font-medium' : 'text-neutral-400'}`}>
            Details
          </span>
          <span className={`text-xs ${step >= 2 ? 'text-[#FF3B30] font-medium' : 'text-neutral-400'}`}>
            Value & Location
          </span>
          <span className={`text-xs ${step >= 3 ? 'text-[#FF3B30] font-medium' : 'text-neutral-400'}`}>
            Review
          </span>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 sm:p-8">
        {/* Step 1: Details */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-1">Giveaway Details</h2>
              <p className="text-neutral-500 text-sm">Tell us about the service you're giving away</p>
            </div>

            <div>
              <Label htmlFor="project_type">Project Type *</Label>
              <Select 
                value={formData.project_type} 
                onValueChange={(v) => setFormData({...formData, project_type: v})}
              >
                <SelectTrigger className={`mt-2 ${errors.project_type ? 'border-red-500' : ''}`} data-testid="select-project-type">
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <span>{type.icon}</span>
                        {type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.project_type && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.project_type}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="title">Giveaway Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Complete Interior Painting Package"
                className={`mt-2 ${errors.title ? 'border-red-500' : ''}`}
                data-testid="input-title"
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.title}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe what the winner will receive. Be specific about what's included..."
                rows={4}
                className={`mt-2 ${errors.description ? 'border-red-500' : ''}`}
                data-testid="input-description"
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.description}
                </p>
              )}
              <p className="text-xs text-neutral-500 mt-1">
                Tip: Include scope (e.g., "up to 5 rooms"), materials, and any exclusions
              </p>
            </div>

            <div>
              <Label htmlFor="contractor_name">Contractor Name (Optional)</Label>
              <Input
                id="contractor_name"
                value={formData.contractor_name}
                onChange={(e) => setFormData({...formData, contractor_name: e.target.value})}
                placeholder="Name of the contractor providing the service"
                className="mt-2"
                data-testid="input-contractor"
              />
            </div>
          </div>
        )}

        {/* Step 2: Value & Location */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-1">Value & Location</h2>
              <p className="text-neutral-500 text-sm">Set the prize value and target location</p>
            </div>

            <div>
              <Label htmlFor="value" className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Prize Value (USD) *
              </Label>
              <Input
                id="value"
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                placeholder="5000"
                className={`mt-2 ${errors.value ? 'border-red-500' : ''}`}
                data-testid="input-value"
              />
              {errors.value && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.value}
                </p>
              )}
              <p className="text-xs text-neutral-500 mt-1">
                Minimum $500. Higher values attract more entries.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  City *
                </Label>
                <Select 
                  value={formData.city} 
                  onValueChange={handleCityChange}
                >
                  <SelectTrigger className={`mt-2 ${errors.city ? 'border-red-500' : ''}`} data-testid="select-city">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {CITIES.map((city) => (
                      <SelectItem key={city.value} value={city.value}>
                        {city.value}, {city.province}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.city && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.city}
                  </p>
                )}
              </div>
              <div>
                <Label>Province/State</Label>
                <Input
                  value={formData.province_state}
                  disabled
                  className="mt-2 bg-neutral-50"
                />
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                End Date *
              </Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                min={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                className={`mt-2 ${errors.end_date ? 'border-red-500' : ''}`}
                data-testid="input-end-date"
              />
              {errors.end_date && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.end_date}
                </p>
              )}
              <p className="text-xs text-neutral-500 mt-1">
                Giveaways must run for at least 7 days
              </p>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                Image URL (Optional)
              </Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://..."
                className="mt-2"
                data-testid="input-image"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Leave blank to use a default image based on project type
              </p>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-1">Review & Launch</h2>
              <p className="text-neutral-500 text-sm">Double-check your giveaway details</p>
            </div>

            {/* Preview Card */}
            <div className="border border-neutral-200 rounded-xl overflow-hidden">
              <div className="relative h-48">
                <img 
                  src={formData.image_url || getDefaultImage(formData.project_type)}
                  alt={formData.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    FREE ENTRY
                  </span>
                  <span className="bg-black/70 text-white text-xs font-bold px-3 py-1 rounded-full">
                    ${parseFloat(formData.value || 0).toLocaleString()} VALUE
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-lg text-neutral-900">{formData.title || 'Giveaway Title'}</h3>
                <div className="flex items-center gap-4 mt-2 text-sm text-neutral-500">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {formData.city}, {formData.province_state}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Ends {new Date(formData.end_date).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-neutral-600 text-sm mt-3 line-clamp-2">{formData.description}</p>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-neutral-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Project Type</span>
                <span className="font-medium">{PROJECT_TYPES.find(t => t.value === formData.project_type)?.label}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Prize Value</span>
                <span className="font-medium text-green-600">${parseFloat(formData.value || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Location</span>
                <span className="font-medium">{formData.city}, {formData.province_state}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">End Date</span>
                <span className="font-medium">{new Date(formData.end_date).toLocaleDateString()}</span>
              </div>
              {formData.contractor_name && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500">Contractor</span>
                  <span className="font-medium">{formData.contractor_name}</span>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 rounded-xl p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">What happens next?</p>
                <ul className="mt-1 space-y-1 text-blue-700">
                  <li>• Your giveaway goes live immediately</li>
                  <li>• Homeowners in {formData.city} can enter for free</li>
                  <li>• You'll receive leads as entries come in</li>
                  <li>• Select a winner when the giveaway ends</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6 border-t border-neutral-100">
          {step > 1 ? (
            <Button variant="outline" onClick={prevStep} data-testid="btn-prev">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="btn-cancel">
              Cancel
            </Button>
          )}
          
          {step < 3 ? (
            <Button onClick={nextStep} className="bg-[#FF3B30] hover:bg-[#DC2626]" data-testid="btn-next">
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={loading}
              className="bg-[#FF3B30] hover:bg-[#DC2626]"
              data-testid="btn-launch"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Launch Giveaway
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Tips Card */}
      <div className="bg-gradient-to-r from-neutral-900 to-neutral-800 rounded-2xl p-6 text-white">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-[#FF3B30]" />
          Tips for Success
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-white/60 mb-1">Higher Values Win</p>
            <p className="text-neutral-300">Giveaways over $5,000 get 3x more entries</p>
          </div>
          <div>
            <p className="text-white/60 mb-1">Be Specific</p>
            <p className="text-neutral-300">Clear descriptions build trust and attract quality leads</p>
          </div>
          <div>
            <p className="text-white/60 mb-1">Time it Right</p>
            <p className="text-neutral-300">2-4 week giveaways perform best</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsorCreateGiveaway;
